import { createAction } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import { isEmpty, castArray } from 'mojito/utils';
import {
    STORE_KEY as PROMOTIONS_STORE_KEY,
    actions as promotionsActions,
} from 'services/promotions/slice.js';
import PromotionUtils from 'services/promotions/utils.js';
import { CONTAINERS_COLLECTION_NAME } from 'services/promotions/types.js';
import {
    STORE_KEY as SPORTS_STORE_NAME,
    actions as sportsActions,
} from 'services/sports-content/sports/slice.js';
import {
    STORE_KEY as EVENTS_STORE_NAME,
    actions as eventsActions,
} from 'services/sports-content/events/slice.js';
import {
    STORE_KEY as EVENT_GROUPS_STORE_NAME,
    actions as eventGroupsActions,
} from 'services/eventgroups/slice.js';
import {
    actions as sportMetaInfoActions,
    STORE_KEY as SPORT_META_INFORMATION_STORE_NAME,
} from 'services/sports-content/sport-meta-information/slice.js';

const reduxInstance = MojitoCore.Services.redux;
const transporterFactory = MojitoCore.Services.transporterFactory;
const { selectLanguage, selectChannel } = MojitoCore.Services.SystemSettings.selectors;

/**
 * Action creator for prefetching data.
 * This action can be dispatched to indicate that the prefetched data is available.
 * The actual handling of this action, such as updating the state or triggering side effects,
 * depends on the reducers and middleware listening for this action.
 *
 * @function prefetchDataSuccess
 * @type {Mojito.Services.redux.ActionCreator}
 *
 * @param {object} payload - Prefetch data.
 * @memberof Mojito.Services.Prefetch.actions
 */

export const actions = {
    prefetchDataSuccess: createAction('prefetch/prefetchDataSuccess'),
};

/**
 * A class for listening to prefetched data. Fills corresponding state slices depending on the data type.
 *
 * @class PrefetchListener
 * @name prefetchListener
 * @memberof Mojito.Services.Prefetch
 */
class PrefetchListener {
    constructor() {
        this.startListening();
    }

    /**
     * Updates a Ngen repository collection with the provided data from prefetch.
     *
     * @param {string} collection - Collection name of ngen repository to update.
     * @param {Array} data - Item data to be added to the collection.
     * @param {string} language - Language code to be used for the collection.
     * @param {boolean} [isLocaleInHeader = true] - Whether the locale is included in the header for this collection.
     * @function Mojito.Services.Prefetch.prefetchListener#updateNgenRepository
     */
    updateNgenRepository(collection, data, language, isLocaleInHeader = true) {
        const transporter = transporterFactory.getTransporter();
        transporter?.registerCollection(collection, language, isLocaleInHeader);
        data.forEach(item => {
            transporter?.addDataToCollection(collection, item, language, true);
        });
    }

    /**
     * Determines if the store content should be seeded with prefetched content.
     *
     * @param {*} storeContent - The current content in the store.
     * @param {*} prefetchContent - The prefetched content to potentially seed.
     * @returns {boolean} True if the store is empty and prefetched content is not empty.
     * @function Mojito.Services.Prefetch.prefetchListener#shouldSeedContent
     */
    shouldSeedContent(storeContent, prefetchContent) {
        return isEmpty(storeContent) && !isEmpty(prefetchContent);
    }

    normalizePrefetchContainers(cmsContainers) {
        if (Array.isArray(cmsContainers)) {
            return cmsContainers;
        }

        return Object.entries(cmsContainers).map(([containerId, data]) => ({
            ...data,
            id: data?.id ?? containerId,
        }));
    }

    /**
     * Attaches listener and start listening for `prefetchDataSuccess` action.
     * Action payload contains prefetched data.
     * When the action is dispatched, it updates the corresponding state slices and Ngen repository collections.
     *
     * @function Mojito.Services.Prefetch.prefetchListener#startListening
     */
    startListening() {
        reduxInstance.actionListener.startListening({
            actionCreator: actions.prefetchDataSuccess,
            effect: ({ payload }, { getState, dispatch }) => {
                const {
                    container,
                    eventGroups,
                    markets,
                    events,
                    promotions,
                    sportsMetaInformation: sportMetaInformation,
                    cmsContainers,
                } = payload;
                const sports = container?.[0]?.items;

                const state = getState();
                const language = selectLanguage(state);
                const eventsState = state[EVENTS_STORE_NAME];
                const sportsState = state[SPORTS_STORE_NAME];
                const promotionsState = state[PROMOTIONS_STORE_KEY];
                const eventGroupsState = state[EVENT_GROUPS_STORE_NAME];
                const sportMetaState = state[SPORT_META_INFORMATION_STORE_NAME];

                const shouldSeedSportMeta = this.shouldSeedContent(
                    sportMetaState.sportMetaInformation,
                    sportMetaInformation
                );
                const shouldSeedSports = this.shouldSeedContent(sportsState.sports, sports);
                const shouldSeedEvents = this.shouldSeedContent(eventsState.events, events);
                const shouldSeedMarkets = this.shouldSeedContent(eventsState.markets, markets);
                const shouldSeedPromotions = this.shouldSeedContent(
                    promotionsState.promotions,
                    promotions
                );
                const shouldSeedEventGroups = this.shouldSeedContent(
                    eventGroupsState.eventGroups,
                    eventGroups
                );

                const shouldSeedCmsContainers = this.shouldSeedContent(
                    promotionsState.containers,
                    cmsContainers
                );

                if (shouldSeedPromotions) {
                    const channel = selectChannel(state).toLowerCase();
                    const promotionForChannel = castArray(promotions).find(p => p.id === channel);
                    if (promotionForChannel) {
                        dispatch(
                            promotionsActions.updatePromotions({
                                data: promotionForChannel,
                                channel,
                            })
                        );
                    }
                    this.updateNgenRepository('promotions', promotions, language, false);
                }

                if (shouldSeedSportMeta) {
                    this.updateNgenRepository(
                        'sportMetaInformation',
                        sportMetaInformation,
                        language
                    );
                    const sportMetaPayload = sportMetaInformation.map(data => ({
                        id: data.id,
                        data,
                    }));
                    dispatch(sportMetaInfoActions.updateSportMetaInformationList(sportMetaPayload));
                }

                if (shouldSeedSports) {
                    dispatch(sportsActions.updateSports({ sports }));
                    this.updateNgenRepository('container', container, language);
                }

                if (shouldSeedEvents) {
                    const eventsPayload = events.map(data => ({ id: data.id, data }));
                    dispatch(eventsActions.updateEvents(eventsPayload));
                    this.updateNgenRepository('events', events, language);
                }

                if (shouldSeedMarkets) {
                    const marketsPayload = markets.map(data => ({ id: data.id, data }));
                    dispatch(eventsActions.updateMarkets(marketsPayload));
                    this.updateNgenRepository('markets', markets, language);
                }

                if (shouldSeedEventGroups) {
                    const eventGroupsPayload = eventGroups.map(data => ({ id: data.id, data }));
                    dispatch(eventGroupsActions.updateEventGroups(eventGroupsPayload));
                    this.updateNgenRepository('eventgroups', eventGroups, language);
                }

                if (shouldSeedCmsContainers) {
                    const channel = selectChannel(state).toLowerCase();
                    const containersToSeed = this.normalizePrefetchContainers(cmsContainers);

                    containersToSeed.forEach(container => {
                        dispatch(
                            promotionsActions.updateContainers({
                                channel: PromotionUtils.createContainerChannelKey(
                                    container.id,
                                    channel
                                ),
                                data: container,
                            })
                        );
                    });

                    const ngenItems = containersToSeed.map(container => ({
                        ...container,
                        id: PromotionUtils.createContainerChannelKey(container.id, channel),
                    }));
                    this.updateNgenRepository(CONTAINERS_COLLECTION_NAME, ngenItems, language);
                }
            },
        });
    }
}

export default new PrefetchListener();
